import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { useFormik } from 'formik'
import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { RequisiteEdit } from '../../core/_models'
import * as Requests from '../../core/_requests'
import { handleAPIErrors } from '../../../../common/ErrorHandler'
import { getCashBoxesLookup } from '../../../../common/core/_requests'
import { ISelectOption } from '../../../../common/types/select'
import { getHideRequisite } from '../../api'
import { cleanUpRequisite } from './utils/cleanUpRequisite'
import { postEnableReservation } from '../../api/postEnableReservation'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'
import { getPaymentMethods } from '../../api/getPaymentMethods'
import { MultiSelect } from '../../../../../components/FormikFields/MultiSelect'
// import { EPaymentMethods } from '../../../../../types/enums'
import { ICashBox } from '../../../../../types'
import { getPaymentMethodLookupWithRequisiteTypes } from '../Requisites/core/api'
import { IPaymentMethodLookupWithRequisiteType } from '../Requisites/core/response.types'
import { buildRequisiteSchema, getAllowedRequisiteTypesIntersection } from './utils/requisiteSchema'
import { isWalletOnly } from './utils/isWalletOnly'
import { clearWalletFields } from './utils/clearWalletFields'

setLocale(ru)

const AppSwal = withReactContent(Swal)

const breadCrumbs: Array<PageLink> = [
  {
    title: 'Реквизиты',
    path: '/settings/requisite',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Создание реквизита',
    path: '/settings/requisite/add',
    isSeparator: false,
    isActive: true,
  },
]



const defaultRequisite: RequisiteEdit = {
  accountNumber: '',
  cashBox: {} as ICashBox,
  cashBoxId: '',
  requisiteType: '',
  cardNumber: '',
  phone: '',
  cardholderName: '',
  comment: '',
  paymentUrl: '',
  paymentLimitByAmount: 150000,
  paymentLimitByTicketCount: 25,
  paymentMethods: [],
  name: '',
  acquiringBank: '',
  minDeposit: '',
  maxDeposit: '',
  fastTrackEnabled: false,
  fastTrackMaxClients: 0,
}

const defaultListCashBoxes: Array<ISelectOption<number>> = []

const EditRequisite: React.FC = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const isEditPage = !!id

  if (isEditPage) {
    breadCrumbs[1]['title'] = 'Редактирование реквизита'
    breadCrumbs[1]['path'] = location.pathname
  }

  const [cashBoxes, setCashBoxes] = useState(defaultListCashBoxes)
  const [requisite, setRequisite] = useState(defaultRequisite)
  const [loading, setLoading] = useState(false)
  const [errorAPI, setErrorAPI] = useState('')
  const [enableReservation, setEnableReservation] = useState<boolean>(false)
  const [defaultEnableReservation, setDefaultEnableReservation] = useState<boolean>(false)

  const cashBoxSelectIsAvailableByRole = useMemo(
    () =>
      currentUser?.roles.includes(ROLE.ROLE_ADMIN) ||
      currentUser?.roles.includes(ROLE.ROLE_MANAGER) ||
      currentUser?.roles.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR) ||
      currentUser?.roles.includes(ROLE.ROLE_MERCHANT_MANAGER),
    [currentUser]
  )

  // ===== UI Состояния =====
  const [initialized, setInitialized] = useState(false)
  const [isInitializing, setInitializing] = useState(false)

  // ===== Справиочники =====
  const [lookupPaymentMethodsWithRequisiteType, setLookupPaymentMethodsWithRequisiteType] = useState<IPaymentMethodLookupWithRequisiteType[]>([])

  const initializPage = async () => {
      setInitializing(true)

      try {
          const [pmRes, cashRes] = await Promise.all([
            getPaymentMethodLookupWithRequisiteTypes(),
            cashBoxSelectIsAvailableByRole
              ? getCashBoxesLookup()
              : Promise.resolve(null),
          ]);

          if (!pmRes?.data?.success) {
            throw new Error("errors" in pmRes?.data ? pmRes.data?.errors?.general_error : "Unknown error");
          }      
          
          setLookupPaymentMethodsWithRequisiteType(pmRes.data.data ?? []);
          
          if (cashBoxSelectIsAvailableByRole && cashRes) {
            if (!cashRes?.data?.success) {
              throw new Error("errors" in cashRes?.data ? cashRes.data?.errors?.general_error : "Unknown error");
            }            
            setCashBoxes(cashRes.data.data ?? [])
          }

          setErrorAPI('')
      } catch (error: any) {
          console.error('Ошибка: ', error)
          setErrorAPI('Возникла ошибка во время загрузки справочников: ' + JSON.stringify(error?.data?.errors))
          setLookupPaymentMethodsWithRequisiteType([])
          setCashBoxes([])
      } finally {
          setInitializing(false)
      }
  }

  const initialValues: RequisiteEdit = {
    ...requisite,
    accountNumber: requisite.accountNumber || '',
    fastTrackEnabled: requisite.fastTrackEnabled || false,
    fastTrackMaxClients: requisite.fastTrackMaxClients || 0,
  }

  const errorStyle = { color: 'red' }

  const validationSchema = useMemo(() => {
    return buildRequisiteSchema(lookupPaymentMethodsWithRequisiteType, !!cashBoxSelectIsAvailableByRole);
  }, [lookupPaymentMethodsWithRequisiteType, cashBoxSelectIsAvailableByRole]);

  const formik = useFormik<RequisiteEdit>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const fields = { ...values }
      if (!fields?.minDeposit) delete fields?.minDeposit
      if (!fields?.maxDeposit) delete fields?.maxDeposit
      setLoading(true);
      saveRequisite(fields).finally(() => setLoading(false));
    },
    enableReinitialize: true,
  })

  const paymentMethods = formik.values.paymentMethods as string[] | undefined;

  const walletOnly = isWalletOnly(formik.values.paymentMethods, lookupPaymentMethodsWithRequisiteType)

  const allowedRequisiteTypes = useMemo(() => {
    return getAllowedRequisiteTypesIntersection(formik.values.paymentMethods, lookupPaymentMethodsWithRequisiteType);
  }, [formik.values.paymentMethods, lookupPaymentMethodsWithRequisiteType]);

  const skipNextPmResetRef = React.useRef(false);
  const apiRequisiteLoadedRef = React.useRef(false);

  const copyId = useCallback(async (value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Скопировано', showConfirmButton: false, timer: 2000 })
      } else {
        Swal.fire({ toast: true, position: 'top', icon: 'info', title: 'Скопируйте вручную', showConfirmButton: false, timer: 3000 })
      }
    } catch {
      Swal.fire({ toast: true, position: 'top', icon: 'info', title: 'Скопируйте вручную', showConfirmButton: false, timer: 3000 })
    }
  }, [])

  useEffect(() => {
    if (!isEditPage || !id) return
    const name = requisite.name || '—'
    const type = requisite.requisiteType || '—'
    document.title = `ID: ${id} | Название: ${name} | Тип: ${type}`
    return () => { document.title = 'CHC | Payment system' }
  }, [id, requisite.name, requisite.requisiteType, isEditPage])

  const getRequisite = async (id: string) => {
    const response = await Requests.getRequisiteAPI(id)

    if (!response.data) {
      return false
    }

    if (response.data.success && response.data.requisite) {
      let requisiteData: any = response.data.requisite
      
      setEnableReservation(requisiteData.enableReservation)
      setDefaultEnableReservation(requisiteData.enableReservation)
      cleanUpRequisite(defaultRequisite, requisiteData)

      skipNextPmResetRef.current = true;
      apiRequisiteLoadedRef.current = true;

      setRequisite({
        ...requisiteData,
        paymentMethods: requisiteData.paymentMethods.map((pm: any) => pm.type),
        acquiringBank: requisiteData.acquiringBank || '',
        cardNumber: requisiteData.cardNumber || '',
        cardholderName: requisiteData.cardholderName || '',
      })

      setErrorAPI('')
    } else {
      setErrorAPI(
        'Возникла ошибка: ' + (response.data.errors ? response.data.errors.general_error : '')
      )
    }
  }

  const saveRequisite = async (values: RequisiteEdit) => {
    const { cashBox, ...rest } = values
    const query = clearWalletFields(rest, lookupPaymentMethodsWithRequisiteType)

    // if (!SHOW_PHONE_FIELD) {
    //   delete query.phone
    // }

    if (requisite.paymentUrl === '' && query.paymentUrl === '') {
      delete query.paymentUrl
    }

    if (requisite.accountNumber === '' && query.accountNumber === '') {
      delete query.accountNumber
    }

    const promisesArray = [Requests.editRequisite(query, id ? id.toString() : '')]

    if (
      isEditPage &&
      IsGranted(
        [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_CASHIER, ROLE.ROLE_SENIOR_CASHIER],
        currentUser
      ) &&
      defaultEnableReservation !== enableReservation
    ) {
      promisesArray.push(postEnableReservation(id as string))
    }

    Promise.all(promisesArray).then((values) => {
      const [editRequisiteRes, enableReservationRes] = values

      setLoading(false)

      if (!editRequisiteRes.data) {
        return false
      }

      if (enableReservationRes && !enableReservationRes?.data) {
        return false
      }

      if (
        editRequisiteRes.data.success &&
        editRequisiteRes.data.requisite &&
        (enableReservationRes ? enableReservationRes.data.success : true)
      ) {
        setDefaultEnableReservation(
          enableReservationRes?.data?.requisite?.enableReservation || enableReservation
        )

        Swal.fire({
          title: `Реквизит успешно ${isEditPage ? 'изменён' : 'создан'}`,
          icon: 'success',
          timer: 1500,
          willClose: () => {
            if (!isEditPage) {
              window.location.replace('/settings/requisite')
            }
          },
        })
      } else if (editRequisiteRes?.data?.errors || enableReservationRes?.data?.errors) {
        const errors = {
          ...editRequisiteRes.data.errors,
          ...(enableReservationRes ? enableReservationRes.data.errors : {}),
        }

        if (editRequisiteRes.data.errors) {
          handleAPIErrors(editRequisiteRes.data.errors, editRequisiteRes.status)
        }

        Object.entries(errors).forEach(([key, value]) => {
          if (key === 'general_error') {
            setErrorAPI('Возникла ошибка: ' + value)
          } else {
            formik.setFieldError(key, value)
          }
        })
      }
    })
  }

  const onPressHide = (e: SyntheticEvent) => {
    e.preventDefault()

    AppSwal.fire({
      title: 'Вы точно хотите навсегда скрыть реквизит?',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Да, скрыть',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await getHideRequisite(id)

          if (response.data && response.data.success) {
            return true
          } else {
            throw new Error(response.data.errors.general_error as string)
          }
        } catch (err: any) {
          AppSwal.showValidationMessage(`Ошибка: ${err.data.errors.general_error}`)
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({
          title: `Реквизит теперь скрыт`,
          timer: 1500,
          willClose: () => {
            window.location.replace('/settings/requisite')
          },
        })
      }
    })
  }


  useEffect(() => {
      if (initialized) return

      const init = async () => {
          await initializPage()
          setInitialized(true)

          if (isEditPage) {
            await getRequisite(id + '')
          }

          if (!cashBoxSelectIsAvailableByRole && currentUser?.cashBox && !isEditPage) {
            formik.setFieldValue('cashBoxId', currentUser.cashBox)
          }
      }

      init()
  }, [initialized])

  useEffect(() => {
    if (!lookupPaymentMethodsWithRequisiteType.length) return;

    const pmList = Array.isArray(formik.values.paymentMethods)
      ? (formik.values.paymentMethods as string[])
      : [];

    const currentRt = formik.values.requisiteType;

    if (skipNextPmResetRef.current) {
      skipNextPmResetRef.current = false;

      if (!pmList.length || !currentRt) return;

      const allowed = getAllowedRequisiteTypesIntersection(pmList, lookupPaymentMethodsWithRequisiteType);
      const allowedCodes = allowed.map((x: any) => x.code);

      if (!allowedCodes.length || !allowedCodes.includes(currentRt)) {
        formik.setFieldValue('requisiteType', '', false);
        formik.setFieldTouched('requisiteType', false, false);
      }

      return;
    }

    formik.setFieldValue('requisiteType', '', false);
    formik.setFieldTouched('requisiteType', false, false);
  }, [formik.values.paymentMethods, lookupPaymentMethodsWithRequisiteType]);

  return (
    <div className='card mb-5 mb-xl-10'>
      <PageTitle breadcrumbs={breadCrumbs}>{breadCrumbs[1]['title']}</PageTitle>
      <div className='card-header border-0'>
        <div
          className='card-title m-0 cursor-pointer'
          role='button'
          data-bs-toggle='collapse'
          data-bs-target='#kt_account_profile_details'
          aria-expanded='true'
          aria-controls='kt_account_profile_details'
        >
          <h3 className='fw-bolder m-0'>{breadCrumbs[1]['title']}</h3>
        </div>
        {isEditPage && (
          <div className='d-flex align-items-center gap-3 text-muted fs-7'>
            <span
              onClick={() => copyId(id!)}
              className='text-primary fw-bold cursor-pointer'
              title='Нажмите для копирования'
            >
              ID: {id || '—'}
            </span>
            <span>| Название: {requisite.name || '—'}</span>
            <span>| Тип: {requisite.requisiteType || '—'}</span>
          </div>
        )}
      </div>

      <div id='kt_account_profile_details' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            {cashBoxSelectIsAvailableByRole && (
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Касса</label>

                <div className='col-lg-8'>
                  <select
                    className='form-select form-select-solid form-select-lg fw-bold'
                    {...formik.getFieldProps('cashBoxId')}
                  >
                    <option value='' key='cashbox' disabled>
                      Выберите кассу
                    </option>
                    {Object.values(cashBoxes).map((value, index) => (
                      <option value={value.value} key={index}>
                        {value.label}
                      </option>
                    ))}
                  </select>
                  {formik.touched.cashBoxId && formik.errors.cashBoxId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.cashBoxId}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                Платежный метод
              </label>
              <div className='col-lg-8 fv-row'>
                <MultiSelect
                  options={lookupPaymentMethodsWithRequisiteType.map((v) => ({ value: v.type, label: v.type}))}
                  setValue={formik.setFieldValue}
                  {...formik.getFieldProps('paymentMethods')}
                />

                {formik.touched.paymentMethods && formik.errors.paymentMethods && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.paymentMethods}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Тип реквизита</label>

              <div className='col-lg-8 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg fw-bold'
                  {...formik.getFieldProps('requisiteType')}
                >
                  <option value='' key='requisiteType' disabled>
                    Выберите тип реквизита
                  </option>
                  {allowedRequisiteTypes.map((rt) => (
                    <option value={rt.code} key={rt.code}>
                      {rt.name ?? rt.code}
                    </option>
                  ))}
                </select>

                {formik.touched.requisiteType && formik.errors.requisiteType && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.requisiteType}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                Название реквизита
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Название реквизита'
                  {...formik.getFieldProps('name')}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.name}</div>
                  </div>
                )}
              </div>
            </div>

            {!walletOnly && (
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Номер карты</label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Номер карты'
                  {...formik.getFieldProps('cardNumber')}
                />
                {formik.touched.cardNumber && formik.errors.cardNumber && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.cardNumber}</div>
                  </div>
                )}
              </div>
            </div>
            )}

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span
                  className={
                    formik?.values.paymentMethods?.filter((el) => el === 'A2A RUB').length > 0
                      ? 'required'
                      : ''
                  }
                >
                  Номер счета
                </span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Номер счета'
                  {...formik.getFieldProps('accountNumber')}
                />
                {formik.touched.accountNumber && formik.errors.accountNumber && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.accountNumber}</div>
                  </div>
                )}
              </div>
            </div>

            {
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                  <span className='required'>Номер телефона</span>
                </label>

                <div className='col-lg-8 fv-row'>
                  <input
                    type='tel'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Номер телефона'
                    {...formik.getFieldProps('phone')}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.phone}</div>
                    </div>
                  )}
                </div>
              </div>
            }

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className={walletOnly ? '' : 'required'}>Держатель</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Держатель'
                  {...formik.getFieldProps('cardholderName')}
                />
                {formik.touched.cardholderName && formik.errors.cardholderName && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.cardholderName}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Комментарий</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Комментарий'
                  {...formik.getFieldProps('comment')}
                />
                {formik.touched.comment && formik.errors.comment && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.comment}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Payment Url</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Payment Url'
                  {...formik.getFieldProps('paymentUrl')}
                />
                {formik.touched.paymentUrl && formik.errors.paymentUrl && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.paymentUrl}</div>
                  </div>
                )}
              </div>
            </div>

            {!walletOnly && (
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Банк-эквайер</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='acquiringBank'
                  {...formik.getFieldProps('acquiringBank')}
                />
                {formik.touched.acquiringBank && formik.errors.acquiringBank && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.acquiringBank}</div>
                  </div>
                )}
              </div>
            </div>
            )}

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Лимит пополнений, 24ч</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Лимит'
                  {...formik.getFieldProps('paymentLimitByAmount')}
                />
                {formik.touched.paymentLimitByAmount && formik.errors.paymentLimitByAmount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.paymentLimitByAmount}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Лимит пополнений по тикетам</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Лимит по тикетам'
                  {...formik.getFieldProps('paymentLimitByTicketCount')}
                />
                {formik.touched.paymentLimitByTicketCount &&
                  formik.errors.paymentLimitByTicketCount && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.paymentLimitByTicketCount}</div>
                    </div>
                  )}
              </div>
            </div>

            {/* !!!! */}

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Минимальный депозит</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='0'
                  {...formik.getFieldProps('minDeposit')}
                  defaultValue={requisite?.minDeposit}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Максимальный депозит</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='1000000'
                  {...formik.getFieldProps('maxDeposit')}
                  defaultValue={requisite?.maxDeposit || 1000000}
                />
              </div>
            </div>

            {isEditPage &&
              IsGranted(
                [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_CASHIER, ROLE.ROLE_SENIOR_CASHIER],
                currentUser
              ) && (
                <div className='row mb-0'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    Разрешить резервирование
                  </label>

                  <div className='col-lg-8 py-3 fs-6'>
                    <div className='form-check form-check-solid form-switch fv-row py-2 mb-4'>
                      <input
                        className='form-check-input w-45px h-30px'
                        type='checkbox'
                        checked={enableReservation}
                        onChange={() => setEnableReservation(!enableReservation)}
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Блок настроек фаст трека */}
            <div className='separator separator-dashed my-8'></div>
            <h2 className='text-dark fw-bolder my-6' style={{fontSize: '1.4rem'}}>Настройки фаст трека</h2>
            
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Можно ли использовать для фаст трека</span>
              </label>

              <div className='col-lg-8 py-3 fs-6'>
                <div className='form-check form-check-solid form-switch fv-row py-2'>
                  <input
                    className='form-check-input w-45px h-30px'
                    type='checkbox'
                    {...formik.getFieldProps('fastTrackEnabled')}
                    checked={formik.values.fastTrackEnabled}
                  />
                </div>
                <div className='form-text mt-5'>
                  Может ли реквизит использоваться в качестве фаст трека
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Максимум клиентов</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='0'
                  min='0'
                  max='999'
                  {...formik.getFieldProps('fastTrackMaxClients')}
                />
                {formik.touched.fastTrackMaxClients && formik.errors.fastTrackMaxClients && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.fastTrackMaxClients}</div>
                  </div>
                )}
                <div className='form-text'>
                  Сколько клиентов рекомендуется подключить к реквизиту (0-999)
                </div>
              </div>
            </div>
          </div>

          {errorAPI && (
            <div className='row mb-6 fw-bold fs-6 border-top p-9' style={errorStyle}>
              {errorAPI}
            </div>
          )}

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            {isEditPage &&
              IsGranted(
                [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR],
                currentUser
              ) && (
                <button className='btn btn-secondary me-3' disabled={loading} onClick={onPressHide}>
                  Скрыть
                </button>
              )}

            <button
              className='btn btn-secondary me-3'
              onClick={() => navigate('/settings/requisite')}
            >
              Отмена
            </button>

            <button type='submit' className='btn btn-primary' disabled={loading}>
              {!loading && 'Сохранить'}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Идёт сохранение...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export { EditRequisite }
